import { useState } from 'react';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Tooltip,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';

import { ReactHookForm } from '../../../../components/hook-form/react-hook-form.component';
import { useReactHookForm } from '../../../../hooks/useReactHookForm';
import { useSaveData } from '../../../../hooks/useSaveData';
import { proctorResultApiKeyStore } from '../../../../api/proctor-result-api-key-store';
import { proctorResultFormSchema } from './proctor-result-form.schema';
import { ProctorResultFormComponent } from './proctor-result-form.component';
import { useAuthorize } from '../../../../hooks/use-authorize';
import { UserPermissionName } from '../../../../models/user-permission-name';

const styling = {
  dialogTitleStyle: { padding: '16px 24px 0' },
  contentPadding: { paddingTop: '30px' },
};

interface ProctorResultDialog {
  proctorData?: any;
  projectID?: any;
  proctorResultsTempEntityID?: string;
  onClosed: () => void;
  onSaveComplete: (data: any) => void;
}

export const ProctorResultDialog = ({
  proctorData,
  projectID,
  proctorResultsTempEntityID,
  onSaveComplete,
  onClosed,
}: ProctorResultDialog) => {
  const form = useReactHookForm({
    defaultValues: {
      ...proctorData,
      // uploadedFileName: proctorData?.uploadedFileName ?? '',
      uploadedFile: proctorData?.uploadedFileName ?? '',
    },
    yupSchema: proctorResultFormSchema,
  });
  const [file, setFile] = useState<File>();
  const { isLoading, mutateAsync } = useSaveData<any>(
    proctorData?.proctorResultsID
      ? proctorResultApiKeyStore.updateProctorResultFiles()
      : proctorResultApiKeyStore.uploadProctorResultFiles()
  );

  const handleSubmit = async (value: any) => {
    const formData = new FormData();
    formData.append('ProctorResultsID', value?.proctorResultsID ?? '');
    formData.append('ProjectID', projectID ?? '');
    formData.append('ProctorFileName', value?.proctorFileName ?? '');
    formData.append('ProctorDate', value?.proctorDate ?? '');
    formData.append('FileNumber', value?.fileNumber ?? '');
    formData.append('DryDensity', value?.dryDensity ?? '');
    formData.append('OptimumMoistureContent', value.optimumMoistureContent ?? '');
    formData.append('VisualSoilCharacteristics', value?.visualSoilCharacteristics ?? '');
    formData.append('UploadedFileName', file ? file.name : value.uploadedFileName ?? '');
    formData.append('UploadedFile', file ?? '');
    formData.append('ProctorResultsTempEntityID', proctorResultsTempEntityID ?? '');
    const data = await mutateAsync({ data: formData });
    onSaveComplete(data.data);
    onClosed();
  };

  const handleFileUpload = async (file: File) => {
    setFile(file);
  };

  const handleSubmitClick = () => {
    form.handleSubmit(handleSubmit)();
  };

  const isReadOnlyPermission = useAuthorize([UserPermissionName?.READ_ONLY]);

  return (
    <Dialog
      open={true}
      fullWidth={true}
      maxWidth={'xl'}
      // onClose={onClosed}
      aria-labelledby="dialog-title"
      aria-describedby="dialog-description"
    >
      <DialogTitle id="dialog-title" style={styling.dialogTitleStyle}>
        {proctorData?.proctorResultsID ? 'EDIT PROCTOR RESULT' : 'ADD PROCTOR RESULT'}
      </DialogTitle>
      <IconButton
        aria-label="close"
        onClick={onClosed}
        sx={{
          position: 'absolute',
          right: 10,
          top: 10,
          color: (theme) => theme.palette.grey[500],
        }}
      >
        <Tooltip title={<Typography variant="body1">Close</Typography>}>
          <CloseIcon />
        </Tooltip>
      </IconButton>
      <ReactHookForm formName="SAVE_CONTACT_FORM" onSubmit={handleSubmit} formContextProps={form} isBusy={isLoading}>
        <DialogContent dividers style={styling.contentPadding} className="popupDilog">
          <ProctorResultFormComponent onFileUpload={handleFileUpload} />
        </DialogContent>
        <div style={{ padding: '5px', paddingLeft: '16px', paddingRight: '16px' }}>
          <DialogActions className="spaceBetween">
            <Button variant="outlined" style={{ width: '130px' }} onClick={onClosed}>
              Cancel
            </Button>
            <Button
              type="button"
              variant="contained"
              onClick={handleSubmitClick}
              disabled={isLoading || isReadOnlyPermission}
              style={{ width: '130px' }}
            >
              Save
            </Button>
          </DialogActions>
        </div>
      </ReactHookForm>
    </Dialog>
  );
};
